import React, { useCallback, useEffect, useState } from 'react';
import { normalizePaginatedResponse, storesApi } from '../services/api';

const emptyForm = {
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: 'Nigeria',
    store_type: 'retail',
};

export default function StoreBranchesPage() {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState(emptyForm);

    const loadStores = useCallback(async () => {
        setLoading(true);
        setError('');
        const response = await storesApi.list({ limit: 500 });
        if (!response.success) {
            setStores([]);
            setError(response.error || 'Could not load branches.');
            setLoading(false);
            return;
        }
        setStores(normalizePaginatedResponse(response).rows);
        setLoading(false);
    }, []);

    useEffect(() => {
        loadStores();
    }, [loadStores]);

    const createBranch = async (event) => {
        event.preventDefault();
        if (!form.name.trim()) {
            window.alert('Branch name is required');
            return;
        }

        setSaving(true);
        const response = await storesApi.create({
            ...form,
            name: form.name.trim(),
            email: form.email.trim() || undefined,
            phone: form.phone.trim() || undefined,
        });
        setSaving(false);

        if (!response.success) {
            window.alert(response.error || 'Could not create branch');
            return;
        }

        setForm(emptyForm);
        await loadStores();
        window.alert('Branch created successfully');
    };

    const updateStatus = async (storeId, status) => {
        const response = await storesApi.update(storeId, { status });
        if (!response.success) {
            window.alert(response.error || 'Could not update branch status');
            return;
        }
        loadStores();
    };

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>Stores & Branches</h2>
                    <p>Dashboard <span className="crumb-sep">›</span> Stores & Branches</p>
                </div>
                <div className="products-toolbar-actions">
                    <button className="mini-icon-btn" type="button" onClick={loadStores}>↻</button>
                </div>
            </div>

            <section className="products-shell stock-shell">
                <div className="panel stock-panel">
                    <h3>Create Branch / Warehouse</h3>
                    <form className="stock-form-grid" onSubmit={createBranch}>
                        <label>
                            <span>Name</span>
                            <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} required />
                        </label>
                        <label>
                            <span>Email</span>
                            <input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} />
                        </label>
                        <label>
                            <span>Phone</span>
                            <input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} />
                        </label>
                        <label>
                            <span>Address</span>
                            <input value={form.address} onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))} />
                        </label>
                        <label>
                            <span>City</span>
                            <input value={form.city} onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))} />
                        </label>
                        <label>
                            <span>State</span>
                            <input value={form.state} onChange={(e) => setForm((prev) => ({ ...prev, state: e.target.value }))} />
                        </label>
                        <label>
                            <span>Country</span>
                            <input value={form.country} onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value }))} />
                        </label>
                        <label>
                            <span>Type</span>
                            <select value={form.store_type} onChange={(e) => setForm((prev) => ({ ...prev, store_type: e.target.value }))}>
                                <option value="retail">Retail Branch</option>
                                <option value="warehouse">Warehouse</option>
                                <option value="outlet">Outlet</option>
                            </select>
                        </label>
                        <div className="stock-form-actions">
                            <button className="btn" type="submit" disabled={saving}>{saving ? 'Creating...' : 'Create Branch'}</button>
                        </div>
                    </form>
                </div>

                <div className="panel stock-panel">
                    <h3>Existing Branches</h3>
                    {error ? <div className="dash-error" style={{ marginBottom: 16 }}><p>{error}</p></div> : null}
                    <div className="table-wrap" style={{ marginTop: 0 }}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Type</th>
                                    <th>Location</th>
                                    <th>Contact</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="dash-empty-row">Loading branches...</td></tr>
                                ) : stores.length === 0 ? (
                                    <tr><td colSpan={6} className="dash-empty-row">No branches found</td></tr>
                                ) : stores.map((store) => (
                                    <tr key={store.id}>
                                        <td>
                                            <strong>{store.name}</strong>
                                            <div className="muted-sm">{store.store_id}</div>
                                        </td>
                                        <td>{store.store_type || 'retail'}</td>
                                        <td>{[store.city, store.state, store.country].filter(Boolean).join(', ') || '-'}</td>
                                        <td>
                                            {store.email || '-'}
                                            <div className="muted-sm">{store.phone || ''}</div>
                                        </td>
                                        <td>{store.status || 'active'}</td>
                                        <td>
                                            <div className="row-actions">
                                                <button className="btn btn-outline btn-sm" type="button" onClick={() => updateStatus(store.id, 'active')}>Activate</button>
                                                <button className="btn btn-outline btn-sm" type="button" onClick={() => updateStatus(store.id, 'inactive')}>Disable</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}
